import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Film,
  Heart,
  Eye,
  MessageCircle,
  BadgeCheck,
  ExternalLink,
  Search,
  Volume2,
  Megaphone,
  Loader2,
  LayoutGrid,
  Instagram,
  Youtube,
  SlidersHorizontal,
} from 'lucide-react';
import './ReelsSearchPage.css';
import { useAuthContext } from './components/AuthProvider';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import ReelAnalysisModal from './components/ReelAnalysisModal';
import { searchTrendingReels, reelMediaUrl } from './lib/api';
import { readReelsCache, writeReelsCache } from './lib/reelsCache';

const RESULT_LIMIT = 30;
const SKELETON_COUNT = 12;
const LOAD_MORE_SKELETONS = 6;

// TikTok has no lucide brand icon — hand-rolled note glyph.
function TikTokGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.5 3c.32 2.13 1.57 3.7 3.66 3.98v2.43c-1.3.13-2.56-.27-3.66-1v5.92a6.05 6.05 0 1 1-6.05-6.05c.31 0 .62.02.92.07v2.62a3.43 3.43 0 1 0 2.51 3.31V3h2.61z" />
    </svg>
  );
}

const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All', Icon: LayoutGrid },
  { value: 'instagram', label: 'Instagram', Icon: Instagram },
  { value: 'tiktok', label: 'TikTok', Icon: TikTokGlyph },
  { value: 'youtube', label: 'YouTube', Icon: Youtube },
];

const PLATFORM_ICONS = {
  instagram: Instagram,
  tiktok: TikTokGlyph,
  youtube: Youtube,
};

const RECENCY_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: 'hour', label: 'Past hour' },
  { value: 'day', label: 'Past 24 hours' },
  { value: 'week', label: 'Past week' },
  { value: 'month', label: 'Past month' },
  { value: 'year', label: 'Past year' },
];

const SORT_OPTIONS = [
  { value: 'default', label: 'Default order' },
  { value: 'views', label: 'Most viewed' },
  { value: 'likes', label: 'Most liked' },
  { value: 'comments', label: 'Most comments' },
  { value: 'newest', label: 'Newest' },
];

const MIN_VIEWS_OPTIONS = [
  { value: 0, label: 'Any views' },
  { value: 10000, label: '10K+ views' },
  { value: 100000, label: '100K+ views' },
  { value: 1000000, label: '1M+ views' },
];

function formatCount(value) {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  // Instagram returns -1 when a count is hidden, and null when unavailable.
  if (Number.isNaN(num) || num < 0) return null;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return `${num}`;
}

function toNumber(value) {
  if (value === null || value === undefined) return -1;
  const num = Number(value);
  return Number.isNaN(num) ? -1 : num;
}

function safeText(value) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (value && typeof value === 'object') {
    const firstTextValue = Object.values(value).find(
      (item) => typeof item === 'string' || typeof item === 'number'
    );
    return firstTextValue == null ? '' : String(firstTextValue);
  }
  return '';
}

function normalizeReel(reel, requestedPlatform) {
  if (!reel || typeof reel !== 'object') return null;
  const platform = safeText(reel.platform).toLowerCase() || requestedPlatform;
  return {
    ...reel,
    id: safeText(reel.id),
    platform,
    username: safeText(reel.username),
    caption: safeText(reel.caption),
    permalink: safeText(reel.permalink),
    video_url: safeText(reel.video_url),
    thumbnail_url: safeText(reel.thumbnail_url),
  };
}

function reelsForPlatform(items, requestedPlatform) {
  const normalized = (Array.isArray(items) ? items : [])
    .map((reel) => normalizeReel(reel, requestedPlatform))
    .filter(Boolean);
  if (requestedPlatform === 'all') return normalized;
  return normalized.filter((reel) => reel.platform === requestedPlatform);
}

function PlatformBadge({ platform }) {
  const Icon = PLATFORM_ICONS[platform];
  if (!Icon) return null;
  return (
    <span className={`reel-card-platform reel-card-platform--${platform}`} title={platform}>
      <Icon size={13} />
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="reel-skeleton" aria-hidden="true">
      <div className="reel-skeleton-media" />
      <div className="reel-skeleton-info">
        <div className="reel-skeleton-line reel-skeleton-line--short" />
        <div className="reel-skeleton-line" />
      </div>
    </div>
  );
}

function ReelCard({ reel, onOpen }) {
  const videoRef = useRef(null);
  const [videoBroken, setVideoBroken] = useState(false);
  const [thumbBroken, setThumbBroken] = useState(false);

  const handleEnter = () => {
    const video = videoRef.current;
    if (video) video.play().catch(() => {});
  };

  const handleLeave = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  const views = formatCount(reel.view_count);
  const likes = formatCount(reel.like_count);
  const comments = formatCount(reel.comment_count);
  const showStats = likes || comments || reel.is_ad;
  // Scraped feeds occasionally ship junk objects where strings are expected —
  // rendering an object as a child crashes the whole page, so only render strings.
  const username = typeof reel.username === 'string' ? reel.username : null;
  const caption = typeof reel.caption === 'string' ? reel.caption : null;

  return (
    <div
      className="reel-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(reel)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(reel);
        }
      }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className="reel-card-media">
        {/* The video's own first frame is the thumbnail (#t=0.1 makes the browser
            paint an early frame), so we never depend on the image CDN. Hover plays it. */}
        {reel.video_url && !videoBroken ? (
          <video
            ref={videoRef}
            src={`${reel.video_url}#t=0.1`}
            muted
            loop
            playsInline
            preload="metadata"
            className="reel-card-thumb"
            onError={() => setVideoBroken(true)}
          />
        ) : reel.thumbnail_url && !thumbBroken ? (
          <img
            src={reelMediaUrl(reel.thumbnail_url)}
            alt=""
            className="reel-card-thumb"
            loading="lazy"
            onError={() => setThumbBroken(true)}
          />
        ) : (
          <div className="reel-card-placeholder">
            <Film size={28} />
          </div>
        )}

        <PlatformBadge platform={reel.platform} />

        <a
          className="reel-card-external"
          href={reel.permalink || undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open original"
          aria-label="Open original"
        >
          <ExternalLink size={13} />
        </a>

        {views && (
          <span className="reel-card-views">
            <Eye size={12} /> {views}
          </span>
        )}
      </div>

      <div className="reel-card-info">
        {username && (
          <span className="reel-card-user">
            @{username}
            {reel.is_verified && <BadgeCheck size={13} className="reel-card-verified" />}
          </span>
        )}
        {caption && <span className="reel-card-caption">{caption}</span>}
        {showStats && (
          <div className="reel-card-stats">
            {likes && (
              <span>
                <Heart size={12} /> {likes}
              </span>
            )}
            {comments && (
              <span>
                <MessageCircle size={12} /> {comments}
              </span>
            )}
            {reel.is_ad && <span className="reel-card-adtag">Ad</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function ReelsSearchPage() {
  const { user, loading } = useAuthContext();

  // Server-side query params (each change triggers a fresh fetch).
  const [platform, setPlatform] = useState('instagram');
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [datePosted, setDatePosted] = useState('');

  // Client-side controls (applied to the already-loaded set — no API call).
  const [sort, setSort] = useState('default');
  const [minViews, setMinViews] = useState(0);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [audioOnly, setAudioOnly] = useState(false);
  const [excludeAds, setExcludeAds] = useState(false);

  // Data + pagination.
  const [reels, setReels] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [activeReel, setActiveReel] = useState(null);

  const requestIdRef = useRef(0);
  const reelsRef = useRef([]);
  const loadingMoreRef = useRef(false);

  const hasKeyword = Boolean(submittedQuery.trim());

  useEffect(() => {
    reelsRef.current = reels;
  }, [reels]);

  // Search runs only on explicit submit (button / Enter), never per keystroke.
  const handleSearch = () => {
    setSubmittedQuery(searchQuery.trim());
  };

  const fetchReels = useCallback(async ({ keyword, recency, plat, cursor, append }) => {
    const requestId = (requestIdRef.current += 1);
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    setError(null);
    try {
      const data = await searchTrendingReels(keyword, {
        limit: RESULT_LIMIT,
        platform: plat,
        datePosted: keyword.trim() ? recency : null,
        cursor,
      });
      if (requestId !== requestIdRef.current) return; // superseded by a newer request
      const incoming = reelsForPlatform(data?.reels, plat);

      if (append) {
        // Some platforms (and the trending feeds) repeat across pages — dedupe.
        const seen = new Set(reelsRef.current.map((r) => r.id || r.permalink));
        const fresh = incoming.filter((r) => !seen.has(r.id || r.permalink));
        setReels((prev) => {
          const merged = [...prev, ...fresh];
          writeReelsCache(user?.id, { platform: plat, keyword, datePosted: recency }, {
            reels: merged,
            nextCursor: data?.next_cursor,
            hasMore: Boolean(data?.has_more) && fresh.length > 0,
          });
          return merged;
        });
        // Stop once a page yields nothing new, even if the API claims more.
        const more = Boolean(data?.has_more) && Boolean(data?.next_cursor) && fresh.length > 0;
        setHasMore(more);
        setNextCursor(more ? data.next_cursor : null);
      } else {
        setReels(incoming);
        const more = Boolean(data?.has_more) && Boolean(data?.next_cursor);
        setHasMore(more);
        setNextCursor(more ? data.next_cursor : null);
        writeReelsCache(user?.id, { platform: plat, keyword, datePosted: recency }, {
          reels: incoming,
          nextCursor: more ? data.next_cursor : null,
          hasMore: more,
        });
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError('Failed to load reels');
      if (!append) setReels([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
        setHasLoaded(true);
      }
    }
  }, [user?.id]);

  // Fetch on mount and whenever platform, keyword, or recency changes.
  useEffect(() => {
    if (loading || !user) return;
    const cached = readReelsCache(user.id, {
      platform,
      keyword: submittedQuery,
      datePosted,
    });
    if (cached) {
      const cachedReels = reelsForPlatform(cached.reels, platform);
      reelsRef.current = cachedReels;
      setReels(cachedReels);
      setNextCursor(cached.nextCursor);
      setHasMore(cached.hasMore);
      setHasLoaded(true);
    } else {
      reelsRef.current = [];
      setReels([]);
      setNextCursor(null);
      setHasMore(false);
      setHasLoaded(false);
    }
    fetchReels({ keyword: submittedQuery, recency: datePosted, plat: platform, cursor: null, append: false });
  }, [user, loading, platform, submittedQuery, datePosted, fetchReels]);

  const handlePlatformChange = useCallback((nextPlatform) => {
    if (nextPlatform === platform) return;
    // Supersede the old platform immediately so its response cannot paint into
    // the newly-selected tab before the next effect starts.
    requestIdRef.current += 1;
    setActiveReel(null);
    setError(null);
    setIsLoading(true);
    setPlatform(nextPlatform);
  }, [platform]);

  const handleLoadMore = useCallback(() => {
    if (loadingMoreRef.current || isLoading || !hasMore || !nextCursor) return;
    loadingMoreRef.current = true;
    fetchReels({
      keyword: submittedQuery,
      recency: datePosted,
      plat: platform,
      cursor: nextCursor,
      append: true,
    }).finally(() => {
      loadingMoreRef.current = false;
    });
  }, [isLoading, hasMore, nextCursor, submittedQuery, datePosted, platform, fetchReels]);

  // Apply sort + filters client-side over the loaded reels.
  const displayed = useMemo(() => {
    let list = reels;
    if (verifiedOnly) list = list.filter((r) => r.is_verified);
    if (audioOnly) list = list.filter((r) => r.has_audio !== false);
    if (excludeAds) list = list.filter((r) => !r.is_ad);
    if (minViews > 0) list = list.filter((r) => toNumber(r.view_count) >= minViews);

    if (sort !== 'default') {
      list = [...list].sort((a, b) => {
        switch (sort) {
          case 'views':
            return toNumber(b.view_count) - toNumber(a.view_count);
          case 'likes':
            return toNumber(b.like_count) - toNumber(a.like_count);
          case 'comments':
            return toNumber(b.comment_count) - toNumber(a.comment_count);
          case 'newest':
            return new Date(b.taken_at || 0) - new Date(a.taken_at || 0);
          default:
            return 0;
        }
      });
    }
    return list;
  }, [reels, verifiedOnly, audioOnly, excludeAds, minViews, sort]);

  const activeFilterCount =
    (verifiedOnly ? 1 : 0) +
    (audioOnly ? 1 : 0) +
    (excludeAds ? 1 : 0) +
    (minViews > 0 ? 1 : 0) +
    (sort !== 'default' ? 1 : 0);

  const resetFilters = () => {
    setSort('default');
    setMinViews(0);
    setVerifiedOnly(false);
    setAudioOnly(false);
    setExcludeAds(false);
  };

  // Show skeletons during any fresh (non-append) load, replacing stale results.
  const showFullLoading = isLoading && reels.length === 0;
  const hiddenByFilters = reels.length - displayed.length;

  return (
    <AuthenticatedLayout>
      <div className="reels-page-content">
        {/* Header */}
        <div className="reels-page-header-section">
          <div className="reels-page-header-left">
            <h1 className="reels-page-title">Trending Reels</h1>
            <p className="reels-page-subtitle">
              Discover trending short videos across Instagram, TikTok, and YouTube.
            </p>
          </div>
        </div>

        {/* Platform selector */}
        <div className="reels-platform-selector" role="tablist" aria-label="Platform">
          {PLATFORM_OPTIONS.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={platform === value}
              className={`reels-platform-btn ${platform === value ? 'reels-platform-btn--active' : ''}`}
              onClick={() => handlePlatformChange(value)}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <form
          className="reels-page-search-row"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <div className="reels-search-field">
            <Search size={16} className="reels-page-search-icon" />
            <input
              type="text"
              className="reels-page-search"
              placeholder="Search Reels — then press Search or Enter…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button type="submit" className="reels-search-btn">
            <Search size={15} />
            Search
          </button>
        </form>

        {/* Filter bar */}
        <div className="reels-filterbar">
          <SlidersHorizontal size={15} className="reels-filterbar-icon" />

          <div className="reels-filterbar-group">
            <label className="reels-control">
              <span className="reels-control-label">Posted</span>
              <select
                className="reels-select"
                value={datePosted}
                disabled={!hasKeyword}
                title={!hasKeyword ? 'Recency applies to keyword searches' : undefined}
                onChange={(e) => setDatePosted(e.target.value)}
              >
                {RECENCY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="reels-control">
              <span className="reels-control-label">Sort</span>
              <select className="reels-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="reels-control">
              <span className="reels-control-label">Views</span>
              <select
                className="reels-select"
                value={minViews}
                onChange={(e) => setMinViews(Number(e.target.value))}
              >
                {MIN_VIEWS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <span className="reels-filterbar-divider" />

          <div className="reels-chip-group">
            <button
              type="button"
              className={`reels-chip ${verifiedOnly ? 'reels-chip--active' : ''}`}
              onClick={() => setVerifiedOnly((v) => !v)}
            >
              <BadgeCheck size={13} /> Verified
            </button>
            <button
              type="button"
              className={`reels-chip ${audioOnly ? 'reels-chip--active' : ''}`}
              onClick={() => setAudioOnly((v) => !v)}
            >
              <Volume2 size={13} /> Has audio
            </button>
            <button
              type="button"
              className={`reels-chip ${excludeAds ? 'reels-chip--active' : ''}`}
              onClick={() => setExcludeAds((v) => !v)}
            >
              <Megaphone size={13} /> No ads
            </button>
          </div>

          {activeFilterCount > 0 && (
            <button type="button" className="reels-reset" onClick={resetFilters}>
              Reset ({activeFilterCount})
            </button>
          )}
        </div>

        {/* Result meta */}
        {hasLoaded && !isLoading && !error && reels.length > 0 && (
          <div className="reels-page-meta">
            <span>
              {displayed.length} {displayed.length === 1 ? 'reel' : 'reels'}
              {hiddenByFilters > 0 && <span className="reels-page-meta-dim"> · {hiddenByFilters} hidden by filters</span>}
              {hasKeyword ? <span className="reels-page-meta-dim"> · "{submittedQuery.trim()}"</span> : <span className="reels-page-meta-dim"> · trending</span>}
            </span>
          </div>
        )}

        {/* Error */}
        {error && <div className="reels-page-error">{error}</div>}

        {/* Empty: nothing returned from API */}
        {!isLoading && hasLoaded && !error && reels.length === 0 && (
          <div className="reels-page-empty">
            <Film size={40} className="reels-page-empty-icon" />
            {hasKeyword ? (
              <p>No reels found for “{submittedQuery.trim()}”.</p>
            ) : (
              <p>No trending reels right now — try again in a moment.</p>
            )}
          </div>
        )}

        {/* Empty: filtered everything out */}
        {!showFullLoading && reels.length > 0 && displayed.length === 0 && (
          <div className="reels-page-empty">
            <Film size={40} className="reels-page-empty-icon" />
            <p>No reels match your filters.</p>
            <button type="button" className="reels-reset" onClick={resetFilters}>
              Clear filters
            </button>
          </div>
        )}

        {/* Grid — skeletons on first load, results otherwise, skeletons appended while loading more */}
        {(showFullLoading || displayed.length > 0) && (
          <div className="reels-page-grid">
            {showFullLoading
              ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonCard key={`sk-${i}`} />)
              : displayed.map((reel, index) => (
                  <ReelCard key={reel.id || reel.permalink || index} reel={reel} onOpen={setActiveReel} />
                ))}
            {isLoadingMore &&
              Array.from({ length: LOAD_MORE_SKELETONS }).map((_, i) => <SkeletonCard key={`skm-${i}`} />)}
          </div>
        )}

        {/* Load more */}
        {hasMore && nextCursor && displayed.length > 0 && !isLoadingMore && (
          <div className="reels-loadmore-row">
            <button type="button" className="reels-loadmore-btn" onClick={handleLoadMore}>
              Load more
            </button>
          </div>
        )}

        {isLoadingMore && (
          <div className="reels-loadmore-row reels-loadmore-row--loading">
            <Loader2 size={15} className="reels-spin" /> Loading more…
          </div>
        )}

        {activeReel && (
          <ReelAnalysisModal reel={activeReel} onClose={() => setActiveReel(null)} />
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default ReelsSearchPage;
